import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import { OperationPartyInfo } from '../types/TradingOfferOperationDetailContextType';

const getPartyInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

type PartySectionProps = {
  party: OperationPartyInfo | null;
  label: string;
  isBuyer: boolean;
};

const PartySection: FC<PartySectionProps> = ({ party, label, isBuyer }) => {
  const { t } = useTranslate();

  return (
    <div className="col-12 col-sm-6">
      <h6 className="overline-title text-soft mb-2">{label}</h6>
      {party ? (
        <>
          <div className="d-flex align-items-center mb-2">
            <div
              className={`user-avatar user-avatar-sm ${
                isBuyer ? 'bg-info-dim' : 'bg-primary-dim'
              } mr-2`}
            >
              <span>{getPartyInitials(party.name)}</span>
            </div>
            <div className="min-w-0 flex-grow-1 overflow-hidden">
              <h6 className="mb-0 text-ellipsis">{party.name}</h6>
              <span className="text-soft fs-13px">
                {t('NrShareholder')}: {party.partnerNumber || '-'}
              </span>
            </div>
          </div>
          <div className="fs-13px">
            <div className="d-flex justify-content-between py-1">
              <span className="text-soft">{t('CurrentShares')}:</span>
              <strong>{formatNumber(party.sharesBefore)}</strong>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-soft">{t('AfterOperation')}:</span>
              <strong className={isBuyer ? 'text-success' : 'text-danger'}>
                {formatNumber(party.sharesAfter)}
              </strong>
            </div>
            <div className="d-flex justify-content-between py-1">
              <span className="text-soft">{t('CapitalAfterOperation')}:</span>
              <strong className={isBuyer ? 'text-success' : 'text-danger'}>
                {party.capitalPercentageAfter.toFixed(2)}%
              </strong>
            </div>
          </div>
        </>
      ) : (
        <p className="text-soft fs-13px">{t('NoInfoAvailable')}</p>
      )}
    </div>
  );
};

type TradingOfferOperationDetailPartiesCardProps = {
  buyerInfo: OperationPartyInfo | null;
  sellerInfo: OperationPartyInfo | null;
};

const TradingOfferOperationDetailPartiesCard: FC<
  TradingOfferOperationDetailPartiesCardProps
> = ({ buyerInfo, sellerInfo }) => {
  const { t } = useTranslate();

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <h6 className="card-title mb-3">
          <em className="icon ni ni-users text-primary mr-2" />
          {t('OperationParties')}
        </h6>
        <div className="row g-4">
          <PartySection party={buyerInfo} label={t('BuyerYou')} isBuyer />
          <PartySection
            party={sellerInfo}
            label={t('Seller')}
            isBuyer={false}
          />
        </div>
      </div>
    </div>
  );
};

export default TradingOfferOperationDetailPartiesCard;
